import React, { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './GenieclassDashboard.css';
import { Container } from 'geniebook-ui/dist';
import MinichatFloatingButtonWrapper from '@components/genieask/MinichatFloatingButtonWrapper';
import WorksheetMinichat from '@pages/genieask/WorksheetMinichatPage';
import {
  setNavbarWithTitleNoTopNavbar,
  resetToDefaultNavbarState,
} from '@atom/navbarAtom';
import { useLocation, useParams } from 'react-router-dom';
import { useViewWidth } from '@utils/helpers/viewWidth';
import UpgradePlanAdvice from '@components/subscription/expired/UpgradePlanAdvice';
import { useIsJCUser } from '@hooks/user.hook';
import { subjectIdFromName } from '@utils/helpers/SubjectParser';
import moment from 'moment';
import { triggerBtn } from '@pages/worksheet/WorksheetDashboardPage';
import { useAtomValue } from 'jotai';
import { subjectSubscriptionsQueryAtom } from '@atom/globalQueryAtom';

const GenieclassDashboard = React.lazy(
  () => import('genieclass/GenieclassDashboard'),
);
const fullChatGenieaskUrl = () => '/genieask';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

export default function GenieclassDashboardPage() {
  const location = useLocation();
  const width = useViewWidth();
  const subscriptions = useAtomValue(subjectSubscriptionsQueryAtom);
  const { subjectId } = useParams();
  const { isJC } = useIsJCUser({
    subjectId: subjectIdFromName(subjectId ?? ''),
  });
  function isSubscriptionExpired() {
    if (
      !subjectId ||
      subscriptions.isLoading ||
      !subscriptions.data?.data.data ||
      subscriptions.data?.data.data.length == 0
    ) {
      return false;
    }

    const selectedSubscription = subscriptions.data.data.data.find(
      item => item.subject_id == subjectIdFromName(subjectId),
    );

    // make sure `selectedSubscription` has `expiry_date` key and is not falsy value (null, empty string, undefined). if, so, treat it as expired sub
    if (
      !selectedSubscription ||
      !('expiry_date' in selectedSubscription) ||
      !selectedSubscription.expiry_date
    ) {
      return true;
    }

    const now = moment();
    const expiryDate = moment(selectedSubscription.expiry_date + '+08:00').add(
      selectedSubscription.subscription_grace_period,
      'day',
    );

    return expiryDate.isBefore(now);
  }

  const getTopicName = () => {
    if (location.search.includes('all=history')) {
      return 'Most Viewed';
    }

    if (location.search.includes('all=upcoming')) {
      return 'Live Class';
    }

    if (location.search.includes('all=recent')) {
      return 'Most Recent';
    }

    const str = location.search;
    const regex = /[?&]all=topic-(.*)-\d+/i;
    const match = str.match(regex);
    if (match && match.length > 1) {
      return decodeURIComponent(match[1].replace(/-/g, ' '));
    } else {
      return null;
    }
  };

  useEffect(() => {
    return () => {
      resetToDefaultNavbarState();
    };
  }, []);

  useEffect(() => {
    const title = getTopicName();
    if (title != null && width < breakpoint.lg) {
      setNavbarWithTitleNoTopNavbar(title);
    } else {
      resetToDefaultNavbarState();
    }
  }, [location.search, width]);

  return (
    <div
      className={`${
        isJC === false ? 'genieclass-dashboard-container' : ''
      } h-[calc(100vh-68px)] max-lg:h-[calc(100vh-110px)] max-md:h-[calc(100vh-100px)]`}
    >
      <Helmet>
        <title>Geniebook - Classes</title>
        <meta name="description" content="Genieclass" />
      </Helmet>
      <Suspense>
        {isSubscriptionExpired() ? (
          <Container className={'mt-12'}>
            <UpgradePlanAdvice />
          </Container>
        ) : (
          <GenieclassDashboard />
        )}
        <Container className="relative bottom-8">
          <MinichatFloatingButtonWrapper>
            <WorksheetMinichat
              trigger={triggerBtn}
              initialScreen="mini-chat"
              fullChatUrl={fullChatGenieaskUrl()}
            />
          </MinichatFloatingButtonWrapper>
        </Container>
      </Suspense>
    </div>
  );
}
